import { useState, Fragment } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from '../../../components/application/defaultComponents'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from 'src/context/renewal'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import { ReviewData } from 'src/dtos/applicationData'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { allActiveAttorneys, hasICOCAttorneys, onlyICOCAttorneys } from 'src/utils/renewals'
import { handleYesNoAnswers, lookupYearsInPrivatePractice } from 'src/utils/formDataManipulation'
import {FILE_SIZE_LABEL} from "../../../utils/constants";

const Attorneys = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { renewalData, saveRenewalData } = useRenewal()
  const [review, setReview] = useState<ReviewData>(renewalData?.attorneysSection?.review ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!renewalData?.attorneysSection?.review?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!renewalData?.attorneysSection?.review?.files)

  const allAttorneys = allActiveAttorneys(renewalData)

  const handleEditClick = () => {
    navigate({
      pathname: '/customer/renew/attorneys/left',
      search: createSearchParams({ review: 'attorneys' }).toString()
    })
  }

  const updatedApplication = () => ({
    ...renewalData,
    stoppedAt: pathname,
    attorneysSection: {
      ...renewalData?.attorneysSection,
      review
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveRenewalData(updatedApplication())
    } catch (_) { }
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...renewalData,
    stoppedAt: pathname,
    attorneysSection: {
      ...renewalData?.attorneysSection,
      review: {
        ...renewalData?.attorneysSection?.review,
        [key]: value
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveRenewalData(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveRenewalData(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveRenewalData(applicationFileAttached)
    }
  }

  const icocAttorneys = onlyICOCAttorneys(renewalData)

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review your attorney information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='attorneysData'>
          <header>
            <div>
              <h4>Attorney</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{ padding: '8px 46px' }} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='attorneys'>
            <h5>ATTORNEYS</h5>
            {allAttorneys.map((attorney, index) => (
              <article key={index}>
                <strong>{[attorney?.firstName, attorney?.lastName].join(' ')}</strong>
                <span><h6>Position:</h6> {attorney?.position}</span>
                <span><h6>Email:</h6> {attorney?.emailAddress}</span>
                {!!attorney?.isNewAttorney && (
                  <>
                    <span>
                      <h6>Years in Private Practice:</h6>
                      {lookupYearsInPrivatePractice(attorney?.yearsInPrivatePractice)}
                    </span>
                  </>
                )}
                <span><h6>License in Texas:</h6> {!!attorney?.license?.isLicensedInTexas ? 'Yes' : 'No'}</span>
                {!!attorney?.license?.isLicensedInTexas && (
                  <>
                    <span><h6>Texas Bar Card Number:</h6> {attorney?.barCardNumber}</span>
                    <span><h6>Date Licensed in Texas:</h6> {attorney?.license?.licenseDate}</span>
                  </>
                )}
                <span><h6>License in Another State:</h6> {attorney?.license?.isLicensedInOtherState ? 'Yes' : 'No'}</span>
                {!!attorney?.license?.isLicensedInOtherState && (
                  <>
                    <span><h6>Other State Bar Card Number:</h6> {attorney?.outSideTexasBarCardNumber}</span>
                    <span><h6>Another State of License:</h6> {attorney?.license?.otherState}</span>
                    <span><h6>License Date in Another State:</h6> {attorney?.license?.dateLicensedInOtherState}</span>
                    <span><h6>Percentage of time "<u>CURRENTLY</u>" practicing in this jurisdiction :</h6> {attorney?.license?.percentageOfWorkSpent}</span>
                    <span><h6>License(s) in Another State Info:</h6> {attorney?.license?.licensesInfo}</span>
                  </>
                )}
                <span><h6>Average hours per week anticipated during the next year for this firm:</h6> {attorney?.workHistory?.hoursPerWeek}</span>
              </article>
            ))}
            {renewalData?.renewalSection?.hasLeavingAttorneys === true ? (
              <>
                <h5>DEPARTING ATTORNEYS</h5>
                {renewalData?.renewalSection?.departingAttorneys?.map((attorney) => (
                  <article key={attorney.attorneyName}>
                    <strong>{attorney?.attorneyName ?? 'N/A'}</strong>
                    <span><h6>Date they left firm:</h6> {attorney?.leavingDate ?? 'N/A'}</span>
                  </article>
                ))}
              </>
            ) : null}
            {allAttorneys.length === 1 ? (
              <>
                <h5>SOLE PRACTITIONER</h5>
                <article>
                  <strong>
                    Average hours worked per week for this firm during the last 12 months, not including work performed as an Independent Contractor or Of Counsel.
                  </strong>
                  <span>{renewalData?.attorneysSection?.solePractitionerLastYear ?? 'N/A'}</span>
                </article>
                <article>
                  <strong>
                    Estimated average hours worked per week for this firm during the next 12 months, not including work performed as an Independent Contractor or Of Counsel.
                  </strong>
                  <span>{renewalData?.attorneysSection?.solePractitionerThisYear ?? 'N/A'}</span>
                </article>
                <article>
                  <strong>
                    If less than 40 hours, please provide the reason:
                  </strong>
                  <span>{renewalData?.attorneysSection?.reasonLessThanFourtyHours ?? 'N/A'}</span>
                </article>
              </>
            ) : null}
            {hasICOCAttorneys(renewalData) === true ? (
              <>
                <h5>INDEPENDENT CONTRACTOR/OF COUNSEL</h5>
                {icocAttorneys?.map(attorney => (
                  <Fragment key={attorney.barCardNumber}>
                    <article>
                      <strong>{[attorney?.firstName, attorney?.lastName].join(' ')}</strong>
                      <span><h6>Year attorney became Independent Contractor or Of Counsel</h6></span>
                      <span>{attorney?.yearStartedAsICOC ?? 'N/A'}</span>
                      <span><h6>In the past 12 months, how many hours did the Attorney bill on behalf of your firm?</h6></span>
                      <span>{attorney?.hoursOnBill ?? 'N/A'}</span>
                      <span><h6>
                        If the attorney has been acting as Independent Contractor or Of Counsel for your firm for less than 12 months, how many hours do you
                        estimate the attorney will bill on behalf of your firm during their first year?
                      </h6></span>
                      <span>{attorney?.hoursBilledInFirstYear ?? 'N/A'}</span>
                      <span><h6>Does the attorney also practice independently from your firm?</h6></span>
                      <span>{handleYesNoAnswers(attorney?.actsIndependentlyFromFirm)}</span>
                      {attorney?.actsIndependentlyFromFirm === true ? (
                        <>
                          <span><h6>Where does the attorney practice independently from your firm? </h6></span>
                          <span>{attorney?.actsIndependentlyFromFirmWhere ?? 'N/A'}</span>
                        </>
                      ) : null}
                      <span><h6>Does the Attorney have professional liability insurance for that independent practice?</h6></span>
                      <span>{handleYesNoAnswers(attorney?.hasInsuranceForIndependentPractice)}</span>
                      {attorney?.hasInsuranceForIndependentPractice === true ? (
                        <>
                          <span><h6>Name of the insurance company? </h6>{attorney?.insuranceCompanyNameOfIndependent ?? 'N/A'}</span>
                          <span><h6>Effective dates of the policy:</h6></span>
                          <span><h6>From: </h6>{attorney?.effectivePolicyDateFrom ?? 'N/A'}</span>
                          <span><h6>To: </h6>{attorney?.effectivePolicyDateTo ?? 'N/A'}</span>
                          <span><h6>Limits of liability of the policy: </h6>{attorney?.liabilityLimitsOfPolicy ? attorney?.liabilityLimitsOfPolicy : 'N/A'}</span>
                        </>
                      ) : null}
                      <span><h6>Which of the following tasks does the attorney perform on behalf of your firm?</h6></span>
                      {(attorney?.tasksPerformed && attorney?.tasksPerformed.length > 0) ?
                        attorney.tasksPerformed.map(task => (
                          <span key={task}>{task ?? 'N/A'}</span>
                        )) : null}
                    </article>
                  </Fragment>
                ))}
              </>
            ) : null}
          </main>
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!renewalData?.attorneysSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={10}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/review/firm"
        nextRoute="/customer/renew/review/questions"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Attorneys